import React, { useEffect, useState } from "react";
import { Panel } from "@glamcorner/components/lib/core/components/Panel";
import { Checkbox } from "@glamcorner/components/lib/core/components/Checkbox";
import useBreakpoints from "@glamcorner/components/lib/utils/useMediaQuery";
import { Plan, Lite, Premium, Ultimate } from "../../components/Plan";
import { PlanInformationMap, planInformationFromBraintreePlanId } from "../../../../common/service/plan";
import { BraintreePlanId } from "@glamcorner/gc-types/lib/subscription";

import "./plansContainer.scss";


type PlansContainerType = {
  plans: PlanInformationMap;
  onSubmit: (planId: BraintreePlanId) => void;
};

type ViewComponentType = {
  planComponentsMap: Record<BraintreePlanId, JSX.Element>;
};

const DesktopViewPlans: React.FC<ViewComponentType> = ({ planComponentsMap }) => (
  <>
    {Object.entries(planComponentsMap).map(([braintreePlanId, component]) => (
      <Panel key={braintreePlanId}>{component as JSX.Element}</Panel>
    ))}
  </>
);

// 99% of complexity in this component comes from those radio buttons on top of the plan selector in mobile view
const MobileViewPlans: React.FC<ViewComponentType> = ({ planComponentsMap }) => {
  const [selectedButtonIndex, setSelectedButtonIndex] = useState(0);
  const [selectedPlan, setSelectedPlan] = useState<BraintreePlanId>(
    Object.keys(planComponentsMap)[selectedButtonIndex]
  );

  useEffect(() => {
    const newBraintreeIDs = Object.keys(planComponentsMap);
    const newBraintreeID = newBraintreeIDs[selectedButtonIndex] || newBraintreeIDs[0];

    setSelectedPlan(newBraintreeID);
  }, [planComponentsMap]);

  return (
    <Panel>
      <div className="choosePlan-plan__mobile-selects">
        {Object.keys(planComponentsMap).map((braintreeId) => {
          const selected = selectedPlan === braintreeId;
          const onSelect = () => {
            const braintreeKeys = Object.keys(planComponentsMap);
            const selectedIndex = braintreeKeys.indexOf(braintreeId);
            setSelectedButtonIndex(selectedIndex);
            setSelectedPlan(braintreeId);
          };

          return (
            <div
              className={`choosePlan-plan__mobile-select ${selected ? "choosePlan-plan__mobile-select--selected" : ""}`}
              onClick={onSelect}
            >
              <Checkbox isSelected={selected} onSelect={onSelect} />
              {planInformationFromBraintreePlanId(braintreeId)?.name}
            </div>
          );
        })}
      </div>

      {planComponentsMap[selectedPlan]}
    </Panel>
  );
};

export const PlansContainer: React.FC<PlansContainerType> = ({ plans, onSubmit }) => {
  const { isMobile } = useBreakpoints();

  const planComponents: Record<BraintreePlanId, JSX.Element> = {};

  Object.keys(plans).map((braintreeId) => {
    const plan = plans[braintreeId];
    const planName = plan.name.toLocaleLowerCase();

    if (planName == "lite") {
      planComponents[braintreeId] = (
        <Lite
          itemLimit={plan.itemLimit}
          tiersAccessible={plan.tiersAccessible}
          price={plan.price}
          onClick={() => onSubmit(braintreeId)}
        />
      );
    } else if (planName == "premium") {
      planComponents[braintreeId] = (
        <Premium
          itemLimit={plan.itemLimit}
          tiersAccessible={plan.tiersAccessible}
          price={plan.price}
          onClick={() => onSubmit(braintreeId)}
        />
      );
    } else if (planName == "ultimate") {
      planComponents[braintreeId] = (
        <Ultimate
          itemLimit={plan.itemLimit}
          tiersAccessible={plan.tiersAccessible}
          price={plan.price}
          onClick={() => onSubmit(braintreeId)}
        />
      );
    } else {
      planComponents[braintreeId] = (
        <Plan
          name={plan.name}
          itemLimit={plan.itemLimit}
          tiersAccessible={plan.tiersAccessible}
          price={plan.price}
          onClick={() => onSubmit(braintreeId)}
        />
      );
    }
  });

  return (
    <div className="choosePlan-PlansContainer__wrapper">
      <div>Step 2: Select a Plan</div>
      <div className="choosePlan-PlansContainer__plans">
        {isMobile ? (
          <MobileViewPlans planComponentsMap={planComponents} />
        ) : (
          <DesktopViewPlans planComponentsMap={planComponents} />
        )}
      </div>
    </div>
  );
};
